import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Palette, Award, Share2 } from 'lucide-react';
import ArtistCard from '../components/woty/ArtistCard';
import VotingCountdown from '../components/woty/VotingCountdown';
import { toast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WotyGallery = () => {
  const [artists, setArtists] = useState([]);
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    Promise.all([
      axios.get(`${API}/contest/artists/public/list`).then((r) => r.data?.rows || []).catch(() => []),
      axios.get(`${API}/contest/config`).then((r) => r.data).catch(() => null),
    ]).then(([rows, config]) => {
      setArtists(rows);
      setCfg(config);
      setLoading(false);
    });
  }, []);

  const votingState = cfg?.votingState || 'open';
  const isOpen = votingState === 'open';
  const scheduled = votingState === 'scheduled';

  const shareGallery = async () => {
    const shareUrl = `${API}/contest/share`;
    const humanUrl = `${window.location.origin}/wine-of-the-year`;
    const data = {
      title: 'Vote for the 2026 Wine of the Year — The Lokal Winery',
      text: "Help pick the artwork for The Lokal Winery's 2026 Wine of the Year label!",
      url: shareUrl,
    };
    if (navigator.share) {
      try { await navigator.share(data); return; } catch (e) { /* cancelled */ }
    }
    try {
      await navigator.clipboard.writeText(humanUrl);
      toast({ title: 'Link copied!', description: 'Share it anywhere — a preview card will show automatically.' });
    } catch (e) {
      window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, '_blank', 'noopener');
    }
  };

  return (
    <div className="min-h-screen" style={{ background: '#fdfbf5' }} data-testid="woty-gallery-page">
      {/* Hero */}
      <section style={{ background: 'linear-gradient(115deg, #722F37 0%, #4A2E1E 100%)' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 text-[#F5EBDC]">
          <p className="text-xs tracking-[3px] font-semibold" style={{ color: '#FDB913' }}>
            THE LOKAL WINERY · 2026 WINE OF THE YEAR
          </p>
          <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05]" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
            Vote for the 2026<br />Wine of the Year
          </h1>
          <p className="mt-6 max-w-2xl text-base sm:text-lg opacity-95 leading-relaxed">
            Submissions are now closed — it's time to choose. The winning artwork becomes the official label
            on The Lokal Winery's 2026 Wine of the Year bottles, celebrating the creativity of Fenelon Falls
            and the communities around it.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-4">
            {cfg && (scheduled ? (
              <VotingCountdown targetIso={cfg.votingOpen} label="Voting opens in" />
            ) : (
              <VotingCountdown targetIso={cfg.votingClose} label="Voting closes in" />
            ))}
          </div>
          <div className="mt-6">
            <button
              onClick={shareGallery}
              data-testid="gallery-share-btn"
              className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-colors"
              style={{ background: '#FDB913', color: '#4A2E1E' }}
            >
              <Share2 className="w-4 h-4" /> Share this contest
            </button>
          </div>
        </div>
      </section>

      {/* Status bar */}
      <div className="border-b" style={{ background: isOpen ? '#FFFBEA' : '#f3ead6', borderColor: '#f0e4c8' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-3 text-sm flex items-center gap-2" style={{ color: '#4A2E1E' }}>
          <Award className="w-4 h-4" style={{ color: '#722F37' }} />
          {isOpen && <span data-testid="voting-status-open"><strong>Voting is open.</strong> Browse the artworks below and cast your one vote.</span>}
          {scheduled && <span data-testid="voting-status-scheduled"><strong>Voting opens soon.</strong> Meet the artists below.</span>}
          {votingState === 'closed' && <span data-testid="voting-status-closed"><strong>Voting has closed.</strong> Thank you to everyone who voted — the winner will be announced shortly.</span>}
        </div>
      </div>

      {/* Holiday gifting CTA */}
      <div style={{ background: '#722F37' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex flex-col sm:flex-row items-center justify-between gap-3 text-center sm:text-left">
          <p className="text-sm sm:text-base text-[#F5EBDC]">
            🎁 <strong>Looking for a gift?</strong> Personalized custom-wine bottles & bottling experiences for Christmas & New Year.
          </p>
          <Link to="/holiday-gifting" data-testid="gallery-holiday-cta"
            className="shrink-0 inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold"
            style={{ background: '#FDB913', color: '#4A2E1E' }}>
            Shop holiday gifts →
          </Link>
        </div>
      </div>

      {/* Grid */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        {loading ? (
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-5">
            {[...Array(6)].map((_, i) => <div key={i} className="aspect-[4/5] rounded-2xl bg-gray-100 animate-pulse" />)}
          </div>
        ) : artists.length === 0 ? (
          <div className="text-center py-16" data-testid="gallery-empty">
            <Palette className="w-10 h-10 mx-auto mb-4" style={{ color: '#722F37' }} />
            <h2 className="text-2xl font-bold text-gray-900" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
              Artists will be revealed soon
            </h2>
            <p className="mt-3 text-gray-600 max-w-md mx-auto">
              The finalists are being prepared. Check back shortly to meet the artists and cast your vote.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-5" data-testid="gallery-grid">
            {artists.map((a) => <ArtistCard key={a.id} artist={a} />)}
          </div>
        )}
      </section>

      <div className="text-center pb-16">
        <Link to="/" className="text-sm font-medium underline-offset-4 hover:underline" style={{ color: '#722F37' }} data-testid="gallery-home-link">
          ← Back to The Lokal
        </Link>
      </div>
    </div>
  );
};

export default WotyGallery;
